import { Component } from '@angular/core';
import { RouterOutlet, Router, ActivatedRoute } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import { Store } from '@ngrx/store';

import {
  CommonConfigService,
  ControllerConnectionService,
  Storage,
  BackupService,
  Broker,
  MonitoredHttpState,
  CONNECTION_STATUS
} from '@c4/core';
import { AuthenticationService, AuthenticationState, AuthenticationStrategy, IdentityService } from '@c4/auth';
import { ToolbarService, ToolbarContext } from '@c4/ui';

const CONTROLLERS = {
  "control4_hc250": "HC-250",
  "control4_hc800": "HC-800",
  "control4_ea1": "EA-1",
  "control4_ea3": "EA-3",
  "control4_ea5": "EA-5",
  "control4_ca1": "CA-1"
}

const TITLES = {
  "programming": "Programming",
  "sysman": "System Manager",
  "about": "Home",
}

@Component({
  selector: 'cw-app',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.less'],
  providers: [ToolbarService]
})
export class AppComponent {

  toolbarContext: Observable<ToolbarContext>;

  private ready: Observable<boolean>;
  private authenticated: Observable<boolean>;

  private subscriptions: Subscription[] = [];

  private connection: Observable<CONNECTION_STATUS>;
  private debouncedConnection: Observable<CONNECTION_STATUS>;

  private CONNECTION_STATUS = CONNECTION_STATUS;
  private rebooting: Observable<boolean>;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private broker: Broker,
    private backups: BackupService,
    private store: Store<{
      authentication: AuthenticationState,
      router: {
        path: string
      },
      monitoredHttpState: MonitoredHttpState
    }>,
    private storage: Storage,
    private toolbarService: ToolbarService,
    private commonConfigService: CommonConfigService,
    private controllerService: ControllerConnectionService,
    private authenticationService: AuthenticationService,
    private identity: IdentityService,
    private title: Title
  ) {

    this.ready = this.store.select(state => state.authentication.initialized);
    this.authenticated = this.store.select(state => state.authentication.authenticated);
    this.connection = this.store.select(state => state.monitoredHttpState.connectionStatus);

    // set initial state and establish a watcher for our online status
    this.storage.set('isOnline', navigator.onLine);
    window.document.addEventListener('onLine', (event: any) => {
      this.storage.set('isOnline', navigator.onLine);
    });

    this.toolbarContext = this.toolbarService.context;

    this.ready = this.store.select(state => state.authentication.initialized);
    this.authenticated = this.store.select(state => state.authentication.authenticated);
    this.connection = this.store.select(state => state.monitoredHttpState.connectionStatus);

    // Introducing a delay here to deal
    // with reflector flakiness.
    this.debouncedConnection = this.connection
      .debounce(status => {
        let interval = (status == CONNECTION_STATUS.DISCONNECTED) ? 5000 : 0;
        return Observable.timer(interval);
      });

    this.rebooting = Observable.combineLatest(
      this.connection,
      this.store.select(s => s.monitoredHttpState.rebootRequested),
      (connection, rebootRequested) => connection == CONNECTION_STATUS.DISCONNECTED && rebootRequested
    );

    // TODO encapsulate this somewhere
    // HACK!! this is to allow the reading of the JWT on teh query string if present
    this.storage.set('isRemote', false);
    // console.log('at app start, browser query string is ', window.location.search);
    this.storage.set('queryString', window.location.search);
    let qs = window.location.search.substring(1).split('&');
    qs.forEach(nv => {
      let parts = nv.split('=');
      this.storage.set(parts[0], parts[1]);
      if (parts[0].toLowerCase() === 'jwt') {
        // console.log('setting provided JWT to ', parts[1]);

        this.storage.set('isRemote', true);
        this.storage.set('authToken', parts[1]);
      }
    });

    Observable.fromPromise(this.broker.call({
      path: '/api/v1/common_name'
    }))
      .map((res: { commonName: string }) => res.commonName.substr(0, res.commonName.lastIndexOf("_")))
      .flatMap(commonName => {
        return this.store.select(s => s.router)
          .filter((r: { path: string }) => !!r && !!r.path)
          .map(r => r.path)
          .map(path => {
            let segments = path.split('/');
            let partial = TITLES[segments[2]] || 'Composer Web';
            return `Control4 ${CONTROLLERS[commonName]} - ${partial}`;
          })
      }).subscribe(title => this.title.setTitle(title));


    // at this point we either have a JWT or we need to auth, so let's check
    // this.authenticationService.validateAuthentication();

    // NOTE when the app is started from scratch, clear the previous
    // temporary "skip welcome" state so the user is redirected to the welcome
    // page again (unless they've opted out)
    this.storage.remove('proceedWithCaution');
  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  logState() {
    this.store.select(s => s)
      .take(1)
      .subscribe(state => {
        console.info(state);
      })
  }
}



// WEBPACK FOOTER //
// ./src/app/app.component.ts